/*
 * Foundation theme: motion layer. Progressive enhancement only; vanilla, no
 * dependencies, loaded with `defer`. CSS does the animating (assets/cinematic.css).
 * This file adds:
 * - [data-reveal] fallback (IntersectionObserver) where CSS scroll-driven animations are missing
 * - <cine-hero> parallax fallback (sets --hero-p) where they are missing
 * - <scroll-story>: active step, image crossfade, counter and progress rail
 * - tier-card tilt and pointer glow (mouse only)
 * - View Transitions: names the clicked product image on "pageswap" (layout/theme.liquid
 *   names the matching image on the next page)
 * Motion effects do nothing when the visitor prefers reduced motion or motion is
 * off in Theme settings. The scroll-story step state is not motion, so it always runs.
 */
(() => {
  const html = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motionOk = () => html.classList.contains('motion-ok') && !reduce.matches;
  const scrollDriven = !!(window.CSS && CSS.supports('animation-timeline: view()'));
  const hasIO = 'IntersectionObserver' in window;

  /* ---------- Reveal on scroll: fallback ---------- */
  let revealObserver;
  const initReveal = (root) => {
    if (scrollDriven || !hasIO || !motionOk()) return;
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px' }
      );
    }
    // Anything already on screen is revealed before hiding is armed, so nothing flashes.
    root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) element.classList.add('is-revealed');
      else revealObserver.observe(element);
    });
    html.classList.add('reveal-armed');
  };

  /* ---------- Hero parallax: fallback ---------- */
  class CineHero extends HTMLElement {
    connectedCallback() {
      if (scrollDriven || !hasIO || !motionOk() || !this.hasAttribute('data-parallax')) return;
      this.measure = () => {
        const rect = this.getBoundingClientRect();
        this.top = rect.top + window.scrollY;
        this.size = rect.height || 1;
      };
      // Same range as the CSS version: from the hero's top reaching the top of the
      // screen (0) to its bottom reaching it (1).
      this.update = () => {
        this.frame = 0;
        const progress = Math.min(1, Math.max(0, (window.scrollY - this.top) / this.size));
        this.style.setProperty('--hero-p', progress.toFixed(4));
      };
      this.onScroll = () => {
        if (!this.frame) this.frame = window.requestAnimationFrame(this.update);
      };
      this.onResize = () => {
        this.measure();
        this.onScroll();
      };
      this.measure();
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('load', this.onResize, { once: true });
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onScroll();
        } else {
          window.removeEventListener('scroll', this.onScroll);
        }
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (!this.observer) return;
      this.observer.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }
  }

  /* ---------- Scroll story ---------- */
  class ScrollStory extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('[data-story-step]'));
      this.frames = Array.from(this.querySelectorAll('[data-story-frame]'));
      this.current = this.querySelector('[data-story-current]');
      this.index = 0;
      if (this.steps.length < 2 || !hasIO) return;
      // A thin band across the middle of the screen: the step crossing it is the active one.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.activate(this.steps.indexOf(entry.target));
          });
        },
        { rootMargin: '-45% 0px -45% 0px' }
      );
      this.steps.forEach((step) => this.observer.observe(step));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    activate(index) {
      if (index < 0 || index === this.index) return;
      this.index = index;
      this.steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
      this.frames.forEach((frame, i) => frame.classList.toggle('is-active', i === index));
      if (this.current) this.current.textContent = String(index + 1).padStart(2, '0');
      this.style.setProperty('--story-index', index);
    }
  }

  /* ---------- Tier cards: tilt and pointer glow (mouse only) ---------- */
  let tiltCard = null;
  let tiltRect = null;
  let tiltFrame = 0;
  const resetTilt = () => {
    if (!tiltCard) return;
    tiltCard.style.removeProperty('--rx');
    tiltCard.style.removeProperty('--ry');
    tiltCard = null;
  };
  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType !== 'mouse' || !finePointer.matches) return;
      const card = event.target instanceof Element ? event.target.closest('[data-tilt]') : null;
      if (card !== tiltCard) {
        resetTilt();
        tiltCard = card;
        tiltRect = card ? card.getBoundingClientRect() : null;
      }
      if (!card) return;
      const x = (event.clientX - tiltRect.left) / tiltRect.width;
      const y = (event.clientY - tiltRect.top) / tiltRect.height;
      window.cancelAnimationFrame(tiltFrame);
      tiltFrame = window.requestAnimationFrame(() => {
        card.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
        card.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
        if (!motionOk()) return;
        card.style.setProperty('--rx', `${((0.5 - y) * 6).toFixed(2)}deg`);
        card.style.setProperty('--ry', `${((x - 0.5) * 8).toFixed(2)}deg`);
      });
    },
    { passive: true }
  );
  document.addEventListener('pointerout', (event) => {
    if (!event.relatedTarget) resetTilt();
  });
  window.addEventListener(
    'scroll',
    () => {
      if (tiltCard) tiltRect = tiltCard.getBoundingClientRect();
    },
    { passive: true }
  );

  /* ---------- Same-page anchor links (e.g. a hero button to /#anchor-id) ----------
     Smooth only here, not globally, so other scripted scrolling stays instant. Focus
     moves to the target so keyboard and screen-reader users land there too. */
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.hash.length < 2) return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    window.history.pushState(null, '', url.hash);
    target.scrollIntoView({ behavior: motionOk() ? 'smooth' : 'auto', block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  /* ---------- View Transitions: product image morph ---------- */
  let lastLink = null;
  document.addEventListener(
    'click',
    (event) => {
      lastLink = event.target instanceof Element ? event.target.closest('a[href]') : null;
    },
    true
  );
  const samePath = (a, b) => {
    try {
      return new URL(a, window.location.href).pathname === new URL(b, window.location.href).pathname;
    } catch (error) {
      return false;
    }
  };
  const onScreen = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  };
  window.addEventListener('pageswap', (event) => {
    if (!event.viewTransition) return;
    const activation = event.activation;
    const destination = activation && activation.entry && activation.entry.url;
    let source = null;
    if (lastLink && destination && activation.navigationType !== 'traverse' && samePath(lastLink.href, destination)) {
      const scope = lastLink.closest('[data-vt-scope]');
      source = scope && scope.querySelector('[data-vt-product]');
    }
    // From a product page, the main image can morph into a card for the same product.
    if (!source) source = document.querySelector('[data-vt-main]');
    if (!source || !onScreen(source)) return;
    try {
      window.sessionStorage.setItem('vt-product', source.getAttribute('data-vt-product'));
    } catch (error) {
      return;
    }
    source.style.viewTransitionName = 'vt-product';
    event.viewTransition.finished.finally(() => {
      source.style.viewTransitionName = '';
    });
  });

  /* ---------- Start ---------- */
  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };
  define('cine-hero', CineHero);
  define('scroll-story', ScrollStory);
  initReveal(document);

  // Theme editor: re-arm reveals in re-rendered sections; show the selected story step.
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const step = event.target.closest && event.target.closest('[data-story-step]');
    const story = step && step.closest('scroll-story');
    if (story && story.activate) story.activate(story.steps.indexOf(step));
  });
})();
